import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CreditCard, Shield, Info, Loader2, KeyRound } from "lucide-react";

export default function PaymentGatewayConfig() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "", enabled: true, test_mode: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/payment-gateway");
    setCfg(data);
    setForm((f) => ({ ...f, razorpay_key_id: data.razorpay_key_id || "", enabled: data.enabled !== false, test_mode: data.test_mode !== false }));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/payment-gateway", form);
      toast.success(`Payment gateway saved — provider: ${data.provider.toUpperCase()}`);
      setForm((f) => ({ ...f, razorpay_key_secret: "", razorpay_webhook_secret: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const provider = cfg?.provider || "mock";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-payment-gateway">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><CreditCard className="w-7 h-7" /> Payment gateway</h1>
        <p className="text-muted-foreground">Configure Razorpay for the public subscription checkout. When keys are missing, the system uses a MOCK gateway that auto-succeeds every payment (useful for demos).</p>
      </div>

      <Card className="p-5 flex items-center justify-between border-2" data-testid="gateway-status">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Active provider</div>
          <div className="mt-1 font-display text-2xl font-black flex items-center gap-2">
            {provider === "razorpay" ? "Razorpay" : "Mock (simulated payments)"}
            <Badge variant={provider === "razorpay" ? "default" : "outline"} className={provider === "razorpay" ? "bg-emerald-500" : ""}>
              {provider === "razorpay" ? "LIVE" : "MOCK"}
            </Badge>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {provider === "razorpay"
              ? `Test mode: ${cfg?.test_mode ? "ON" : "OFF"} · Key ID: ${cfg?.razorpay_key_id || "—"}`
              : "Add Razorpay Key ID + Secret below and the system will switch to real Razorpay checkout automatically."}
          </div>
        </div>
        <Shield className={`w-12 h-12 ${provider === "razorpay" ? "text-emerald-500" : "text-amber-500"}`} />
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2 font-display text-lg font-bold"><KeyRound className="w-5 h-5" /> Razorpay credentials</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Key ID</Label>
            <Input placeholder="rzp_test_xxxxxxxxxxxxxxx" value={form.razorpay_key_id} onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })} data-testid="gw-key-id" />
          </div>
          <div className="space-y-2">
            <Label>Key Secret</Label>
            <Input type="password" placeholder={cfg?.razorpay_key_secret_masked ? `Saved · ${cfg.razorpay_key_secret_masked}` : "Paste secret to update"} value={form.razorpay_key_secret} onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })} data-testid="gw-key-secret" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Webhook secret <span className="text-muted-foreground">(optional — required to receive webhook events)</span></Label>
            <Input type="password" placeholder={cfg?.razorpay_webhook_secret_set ? "Saved · leave blank to keep" : "Paste webhook secret"} value={form.razorpay_webhook_secret} onChange={(e) => setForm({ ...form, razorpay_webhook_secret: e.target.value })} data-testid="gw-webhook-secret" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><div className="font-semibold">Enabled</div><div className="text-xs text-muted-foreground">Turn off to fall back to MOCK gateway.</div></div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="gw-enabled" />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><div className="font-semibold">Test mode</div><div className="text-xs text-muted-foreground">Use Razorpay test keys (rzp_test_*). Toggle off for production.</div></div>
            <Switch checked={form.test_mode} onCheckedChange={(v) => setForm({ ...form, test_mode: v })} data-testid="gw-test-mode" />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={busy} data-testid="gw-save" className="h-11 px-6">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save & activate
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-blue-500/30 bg-blue-500/[0.03]">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">How it works</div>
            <ul className="mt-2 space-y-1 text-muted-foreground list-disc list-inside">
              <li>While keys are empty → all checkouts run in <b>MOCK</b> mode and register users straight into the Super Admin approval inbox.</li>
              <li>The moment you paste a valid Key ID + Secret and save, Razorpay checkout activates for every new registration — zero code changes needed.</li>
              <li>Grab keys from your <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline text-blue-500">Razorpay Dashboard → Settings → API Keys</a>.</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
